import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'react-bootstrap'
import DatePicker from 'react-datepicker'

import FilterPagination from 'pages/parts/FilterPagination'
import { GET, httpOk } from 'helpers/api'
import { formatDate, formatMoney, chequeDates, getCurrentChequeDate, checkFilter } from 'helpers/helpers'

function DeletedExpenses() {
	const { t } = useTranslation()
	const date = new Date()

	const initialFilter = {
		start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		end_date:   formatDate(new Date(), 'yyyy-MM-dd'),
		search:     '',
	}

	const [data, setData]                         = useState({ data: [], total: 0, total_amount: 0 })
	const [filterData, setFilterData]             = useState({ ...initialFilter })
	const [filterDataExist, setFilterDataExist]   = useState(false)
	const [dropdownIsOpen, setDropdownIsOpen]     = useState(false)
	const [pagination, setPagination]             = useState({ url: '/report/deleted-expenses', response: null })

	async function getData() {
		setFilterDataExist(checkFilter(filterData, initialFilter))
		const response = await GET('/report/deleted-expenses', filterData, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, response: response.data })
			if (dropdownIsOpen) setDropdownIsOpen(false)
		}
	}

	async function paginate(page, size) {
		setFilterData({ ...filterData, page, size })
	}

	function resetFilter() {
		setFilterData({ ...initialFilter })
	}

	function selectChequeDates(option) {
		const result = getCurrentChequeDate(option.id)
		setFilterData({ ...filterData, ...result, dateType: option.id })
	}

	useEffect(() => {
		getData()
	}, [filterData.page, filterData.size]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('deleted_expenses')} [{data.total}]
				</h4>
				<div className="vertical-center">
					<span className="text-muted me-2">{t('total')}:</span>
					<span className="text-danger fw-bold fs-5">{formatMoney(data.total_amount)}</span>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between mb-3">
						<div className="d-flex gap-2">
							<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
									{t('filter')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-4">
											<label>{t('period')}</label>
											<select className="form-select"
												onChange={(e) => selectChequeDates({ id: e.target.value })}>
												<option value="">{t('select')}</option>
												{chequeDates.map((item, index) => (
													<option key={index} value={item.id}>{item.name}</option>
												))}
											</select>
										</div>
										<div className="form-group col-md-4">
											<label className="invisible">{t('period')}</label>
											<div className="calendar-input">
												<DatePicker
													className="form-control"
													dateFormat="dd.MM.yyyy"
													selected={filterData.start_date ? new Date(filterData.start_date) : ''}
													onChange={(d) => setFilterData({ ...filterData, start_date: formatDate(d, 'yyyy-MM-dd') })} />
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
										<div className="form-group col-md-4">
											<label className="invisible">.</label>
											<div className="calendar-input">
												<DatePicker
													className="form-control"
													dateFormat="dd.MM.yyyy"
													selected={filterData.end_date ? new Date(filterData.end_date) : ''}
													onChange={(d) => setFilterData({ ...filterData, end_date: formatDate(d, 'yyyy-MM-dd') })} />
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
										<div className="form-group col-md-12">
											<label>{t('search')}</label>
											<input type="text" className="form-control"
												value={filterData.search}
												onChange={(e) => setFilterData({ ...filterData, search: e.target.value })} />
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12 d-flex justify-content-end gap-2">
											<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>
												{t('cancel')}
											</button>
											<button className="btn btn-primary" onClick={getData}>
												{t('apply')}
											</button>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>

							{filterDataExist &&
								<button className="btn btn-outline-dark" onClick={resetFilter}>
									{t('reset')}
								</button>
							}
						</div>
					</div>

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ minWidth: '33px' }} className="text-center">#</th>
									<th style={{ width: '12%' }}>{t('type')}</th>
									<th style={{ width: '10%' }}>{t('payment_type')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('amount')}</th>
									<th style={{ width: '15%' }}>{t('note')}</th>
									<th style={{ width: '10%' }}>{t('cashier')}</th>
									<th style={{ width: '12%' }} className="text-center">{t('date')}</th>
									<th style={{ width: '10%' }}>{t('deleted_by')}</th>
									<th style={{ width: '15%' }}>{t('delete_note')}</th>
									<th style={{ width: '12%' }} className="text-center">{t('deleted_at')}</th>
								</tr>
							</thead>
							<tbody>
								{data.data?.length === 0 ? (
									<tr>
										<td colSpan="10" className="text-center text-muted py-4">{t('list_empty')}</td>
									</tr>
								) : (
									data.data?.map((item, index) => (
										<tr key={index}>
											<td className="text-center">{index + 1}</td>
											<td className="td-ellipsis">{item.purpose_name}</td>
											<td className="td-ellipsis">{item.payment_type_name}</td>
											<td className={`text-end ${item.transaction_type === 'plus' ? 'text-success' : 'text-danger'}`}>
												{formatMoney(item.amount)}
											</td>
											<td className="td-ellipsis" title={item.note}>{item.note}</td>
											<td className="td-ellipsis">{item.created_by_name}</td>
											<td className="text-center text-nowrap">{formatDate(item.created_at, 'dd.MM.yyyy HH:mm')}</td>
											<td className="td-ellipsis">{item.deleted_by_name}</td>
											<td className="td-ellipsis" title={item.deleted_note}>{item.deleted_note}</td>
											<td className="text-center text-nowrap">{formatDate(item.deleted_at, 'dd.MM.yyyy HH:mm')}</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination pagination={pagination} paginate={paginate} />
					}
				</div>
			</div>
		</>
	)
}

export default DeletedExpenses
